<template>
	<div>
		<!-- Empty -->
		<j-card
			v-if="!clausesInOffer || clausesInOffer.length === 0"
			class="bg5 p3 fs3 flex xs-center"
		>
			{{ $t("errors.no_data") }}
		</j-card>

		<!-- List of selected clauses -->
		<div v-else class="clause-list bg4 p4">
			<j-card
				v-for="(clause, i) in clausesInOffer"
				:key="i"
				class="flex xs-12 xs-column bg5 p2 mb2"
			>
				<!-- Top -->
				<div class="flex xs-12 xs-column mb2">
					<!-- Name -->
					<div class="flex xs-middle mb2">
						<p class="mr1 fs3 c6">{{ `${$t("name")}:` }}</p>
						<p class="fs3">{{ clause.template.name }}</p>
					</div>
					<!-- renderedHtmlResult -->
					<div class="flex xs-column">
						<p class="mr1 fs3 mb1 c6">{{ `${$t("description")}:` }}</p>
						<div
							class="fs3 lh150"
							v-html="clause.htmlOverride || clause.renderedHtmlResult.value"
						></div>
					</div>
				</div>
				<!-- Bottom -->
				<div class="flex xs-12 xs-right xs-middle bc7 bt1 pt2">
					<j-button
						size="xsmall"
						type="secondary"
						@click="onUpdate(clause)"
						class="mr1"
					>
						<crm-icon name="edit" size="small" color="c5" />
						{{ $t("update") }}
					</j-button>
					<j-button size="xsmall" type="error" @click="onRemove(clause)">
						<crm-icon name="delete" size="small" color="c5" />
						{{ $t("remove") }}
					</j-button>
				</div>
			</j-card>
		</div>

		<!-- Add Button -->
		<div class="flex xs-right xs-12 mt2">
			<j-button size="xsmall" @click="$modal.show('addClauseToProposal')">
				{{ $t("add_clause") }}
			</j-button>
		</div>

		<!-- Add Modal -->
		<core-modal
			name="addClauseToProposal"
			:headline="$t('add_clause')"
			:pivotX="0"
			:pivotY="0"
			:hasHeader="false"
		>
			<clause-add
				:caseId="caseId"
				:proposalId="proposalId"
				:offerId="offerId"
				@added="afterAdd"
			/>
		</core-modal>

		<!-- Update Modal -->
		<core-modal
			name="updateClauseOfProposal"
			:headline="$t('update_clause')"
			:pivotX="0"
			:pivotY="0"
			:hasHeader="false"
		>
			<clause-update
				:caseId="caseId"
				:proposalId="proposalId"
				:offerId="offerId"
				:clause="selectedClause"
				@updated="afterUpdate"
			/>
		</core-modal>
	</div>
</template>

<script>
import JCard from "@/components/JCard"
import JButton from "@/components/JButton"
import CrmIcon from "@/components/CrmIcon"
import CoreModal from "@/modules/core/components/CoreModal"

import ClauseAdd from "./ClauseAdd"
import ClauseUpdate from "./ClauseUpdate"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import get from "lodash.get"

export default {
	props: {
		caseId: {
			type: String
		},
		proposalId: {
			type: String
		},
		offerId: {
			type: String
		}
	},

	components: {
		JCard,
		CrmIcon,
		JButton,
		CoreModal,

		ClauseAdd,
		ClauseUpdate
	},

	data() {
		return {
			clausesInOffer: [],
			selectedClause: null
		}
	},

	methods: {
		async onRemove(clause) {
			const query = gql`
				mutation removeClauseFromProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$clauseId: String!
				) {
					removeClauseFromProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						clauseId: $clauseId
					) {
						status
						errors
					}
				}
			`
			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				offerId: this.offerId,
				clauseId: clause.id
			}
			const res = await api({ query, variables })
			if (res?.data?.removeClauseFromProposalOffer?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.removeClauseFromProposalOffer?.errors?.[0] || [
						this.$t("errors.general")
					]
				)
				return
			} else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("clause_removed")
					}
				})
			this.$store.commit("caseManagement/publish", {
				eventName: "clauses:updated",
				data: null
			})
			this.fetchClausesForOffer()
		},

		onUpdate(clause) {
			this.selectedClause = clause
			this.$modal.show("updateClauseOfProposal")
		},

		async fetchClausesForOffer() {
			const query = gql`
				query fetchClausesInOffer($where: caseWhere) {
					cases(where: $where) {
						list {
							id
							proposals {
								id
								basket {
									id
									clauses {
										id
										order
										htmlOverride
										template {
											id
											name
											graphQlQuery
											... on clauseHtml {
												html
											}
										}
										renderedHtmlResult {
											value
										}
										renderParameters {
											contentJsonString
											variablesJsonString
										}
									}
								}
							}
						}
					}
				}
			`
			const variables = { where: { id: this.caseId } }
			const res = await api({ query, variables })

			this.clausesInOffer = res?.data?.cases?.list?.[0]?.proposals
				?.find(item => item.id === this.proposalId)
				?.basket?.find(item => item.id === this.offerId)
				?.clauses?.sort((a, b) => a.order - b.order)
		},

		afterAdd() {
			this.$modal.hide("addClauseToProposal")
			this.fetchClausesForOffer()
			this.$store.commit("caseManagement/publish", {
				eventName: "clauses:updated",
				data: null
			})
		},

		afterUpdate() {
			this.$modal.hide("updateClauseOfProposal")
			this.fetchClausesForOffer()
			this.$store.commit("caseManagement/publish", {
				eventName: "clauses:updated",
				data: null
			})
		}
	},

	async created() {
		await this.fetchClausesForOffer()
	},

	watch: {
		proposalId: function() {
			this.fetchClausesForOffer()
		},
		offerId: function() {
			this.fetchClausesForOffer()
		}
	}
}
</script>

<style scoped>
.clause-list {
	max-height: 300px;
	overflow: scroll;
}
</style>
